import {
    Box,
    BoxProps,
    Drawer,
    DrawerBody,
    DrawerContent,
    DrawerOverlay,
    HStack,
    Menu,
    MenuProps,
    MenuButton,
    MenuItem,
    MenuList,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalProps,
    ModalFooter,
    ModalOverlay,
    Spinner,
    Text,
    useBreakpointValue,
    useDisclosure
} from "@chakra-ui/react";
import {DateRange, DayPicker, DayPickerProps} from "react-day-picker";
import {ReactNode, useMemo, useState} from "react";
import {DatePickerProps, DateRangeProps} from "../../helper/types";
import {L5Button} from "./CustomButton"
import {CustomTimePicker, WheelDateTimePicker} from "../UI";
import "react-day-picker/style.css";
import {FormInput} from "./CustomInput";
import {notEmpty} from "../../helper/utils";

interface ConfirmationModalProps extends Omit<ModalProps, "children"> {
    title?: string
    description: ReactNode
    icon?: string
    children?: ReactNode
    hideIcon?: boolean
    iconColor?: string
}
interface ModalFormProps extends Omit<ModalProps, "children"> {
    title?: string
    children?: ReactNode
    footer?: ReactNode
};
interface CustoMenuProps extends Omit<MenuProps, "children">  {
    children?: ReactNode
    items: {action?: VoidFunction, icon: string, content: string}[]
}
type SuccessModalProps = {
  onClose: VoidFunction;
  isOpen: boolean;
  title: string;
  description: string;
  mainButtonText?: string;
  onMainButtonClick?: VoidFunction;
  trailerComponent?: JSX.Element;
};
interface ConfirmationProps<T> {
    buttonType?: "primary" | "secondary"
    buttonLabel: string
    cancelLabel?: string
    description?: ReactNode
    error?: string
    hideIcon?: boolean
    icon?: string;
    isLoading?: boolean
    isDisabled?: boolean
    inputTransformer?: (value: T) => T
    modalTitle: string;
    onSubmit: (value: T) => void
    placeholder?: string
    submitLabel?: string
    size?: string
    validator?: (v: T) => string | undefined
}

const bodyProps: BoxProps = {
    px:{base: "1rem", md: "2rem"},
    py: "1rem",
    maxW: {base: "100%"},
    borderRadius: "12px",
    display: "flex",
    flexDir: "column",
    gap: "1rem",
    alignItems: "center",
};
type RangePickerProps = DateRangeProps & { onClose: () => void;
    isOpen: boolean;
    onDateTimeSelected?: (date: Date) => void;
    selectedDateTime?: Date;
    disabled?: DayPickerProps["disabled"]
} ;

type TimeProps = DatePickerProps & {onClose: () => void, isOpen: boolean};

function CustomDatePicker(props: DatePickerProps) {
    const today = new Date();
    const [month, setMonth] = useState<Date>();
    return <Box minH="370px" className="date-picker">
        <DayPicker
            mode="single"
            captionLayout="dropdown"
            defaultMonth={props.selectedDate}
            onSelect={(date) => {
                if (date) {
                    props.onSelectDate(date);
                }
            }}
            selected={props.selectedDate}
            onMonthChange={setMonth}
            month={month}
            startMonth={today}
            endMonth={new Date(today.getFullYear() + 3, 12)}
            showOutsideDays
            disabled={props.disabled}
        />
    </Box>;
}

export function TimeModal(p: TimeProps) {
    const selectedHour = p.selectedDate?.getHours() ?? 0;
    const meridian = selectedHour >= 12 ? "PM" : "AM";
    const [time, setTime] = useState({
        hour: (meridian === "PM" ? selectedHour - 12 : selectedHour)
        .toString()
        .padStart(2, "0"),
        minute: p.selectedDate?.getMinutes().toString() ?? "00",
        ampm: meridian,
    });
    const [day, setDay] = useState(p.selectedDate);
    const isMobile = useBreakpointValue({base: true, md: false});
    function handleSave(pickedDate: Date | undefined) {
        let hour: number;
        const defaultDate = !day && !isMobile ? new Date() : day!;
        const selectedDateTime = new Date(pickedDate ?? defaultDate);
        if (time.hour && time.minute && !pickedDate) {
            // Detect midnight and noon
            if (time.hour === "12" && time.ampm === "AM") {
                hour = 0;
            } else if (time.hour === "12" && time.ampm === "PM") {
                hour = 12;
            } else {
                hour =
                    time.ampm === "PM"
                        ? parseInt(time.hour) + 12
                        : parseInt(time.hour);
            }
            selectedDateTime.setHours(hour, parseInt(time.minute, 10), 0);
            p.onSelectDate(selectedDateTime);
        } else {
            p.onSelectDate(selectedDateTime);
        }
        p.onClose();
    }
    function TimeHeader() {
        return <DefaultDescription
            title="Date And Time Selection"
            desc="Select the pick-up date and time for this load"
            alignSelf="start"
        />;
    }
    function TimeDescription () {
        return <>
            <TimeHeader/>
            <HStack
                alignSelf="stretch"
                display="flex"
                gap="16px"
                flexWrap="wrap"
                align="start"
                justify="space-between"
            >
                <CustomDatePicker
                    onSelectDate={setDay}
                    selectedDate={day ?? new Date()}
                    disabled={p.disabled}
                />
                <CustomTimePicker time={time} onTimeChange={setTime}/>
            </HStack>
        </>;
    }
    return <>
        {!isMobile && <ConfirmationModal
            onClose={p.onClose}
            isOpen={p.isOpen}
            hideIcon
            size="2xl"
            description={<TimeDescription/>}
        >
            <HStack
                justifyContent={{md: "flex-end"}}
                gap="1rem"
                flexDir={{base: "column", md: "row"}}
                align={{base: "stretch", md: "center"}}
            >
                <L5Button type="secondary" onClick={p.onClose} flex="0 1 170px">
                    Cancel
                </L5Button>
                <L5Button type="primary" onClick={() => handleSave(undefined)} flex="0 1 170px">
                    Save
                </L5Button>
            </HStack>
        </ConfirmationModal>}
        {isMobile && <Drawer isOpen={p.isOpen} placement="bottom" onClose={p.onClose}>
            <DrawerOverlay />
            <DrawerContent borderRadius="12px" px="16px" pb="44px">
                <DrawerBody px={0}>
                    <TimeHeader/>
                    <WheelDateTimePicker
                        onSelectDateTime={handleSave}
                        selectedDate={p.selectedDate}
                    />
                </DrawerBody>
            </DrawerContent>
        </Drawer>}
    </>;
}

export function DateRangeModal(props: RangePickerProps) {
    const [range, setRange] = useState<DateRange | undefined>(props.dateRange);
    const isMobile = useBreakpointValue({base: true, md: false});
    function handleSave(val?: DateRange) {
        if ((!val && isMobile) || ((!range?.from || !range.to) && !isMobile)) {
            props.onClose();
            return;
        }
        props.onSelectDateRange({from: val?.from, to: val?.to});
        props.onClose();
    }
    function RangeDescription () {
        return <>
            <DefaultDescription
                title="Pick-up Date and Time"
                desc="Select the pick-up date and time for this load"
                alignSelf="start"
            />
            <Box minH="370px" className="daterange-picker" alignSelf="stretch" flex="2 1 auto" overflowY="auto">
                <DayPicker
                    mode="range"
                    numberOfMonths={2}
                    disabled={props.disabled}
                    onSelect={(dateRange) => {
                        if (dateRange?.from && dateRange?.to) {
                            setRange(dateRange);
                        }
                    }}
                    selected={range}
                />
            </Box>
        </>;
    }
    return <ConfirmationModal
        isOpen={props.isOpen}
        onClose={props.onClose}
        hideIcon
        description={<RangeDescription/>}
        size="3xl"
    >
        <HStack
            justifyContent={{md: "flex-end"}}
            gap="1rem"
            flexDir={{base: "column", md: "row"}}
            align={{base: "stretch", md: "center"}}
        >
            <L5Button type="secondary" onClick={props.onClose} flex={{base: "0 0 auto", md: "0 1 170px"}}>
                Cancel
            </L5Button>
            <L5Button type="primary" onClick={() => handleSave(range)} flex={{base: "0 0 auto", md: "0 1 170px"}}>
                Save
            </L5Button>
        </HStack>
    </ConfirmationModal>;
}

export function ModalForm(props: ModalFormProps) {
    return <Modal
        isOpen={props.isOpen}
        onClose={props.onClose}
        isCentered
        trapFocus
        size={props.size ?? "md"}
    >
        <ModalOverlay/>
        <ModalContent zIndex="overlay">
            {props.title && <ModalHeader>{props.title}</ModalHeader>}
            <ModalCloseButton insetInlineEnd={{base:"1rem", md: "2rem"}} insetBlockStart=".75rem"/>
            <ModalBody {...bodyProps}>
                {props.children}
            </ModalBody>
            {props.footer && <ModalFooter
                flexDir="column-reverse"
                alignItems="stretch"
                gap=".5rem"
                px={{base: "1rem", md: "2rem"}}
            >{props.footer}</ModalFooter>}
        </ModalContent>
    </Modal>;
}

export function ConfirmationModal(props: ConfirmationModalProps) {
    return <ModalForm
        isOpen={props.isOpen}
        onClose={props.onClose}
        size={props.size}
        title={props.title}
        footer={props.children}
    >
        {!props.hideIcon && <svg width="116" height="116" style={{color: props.iconColor ?? "var(--chakra-colors-red-400)"}}>
            <use xlinkHref={`#${props.icon ?? "warning-thin"}`}/>
        </svg>}
        {props.description}
    </ModalForm>;
}

function DefaultDescription(props: {title:string, desc: string} & BoxProps) {
    return <Box className="stack" lineHeight="1.375" textAlign={props.textAlign} css={{"--s-gap": props.gap ?? ".5rem"}} alignSelf={props.alignSelf}>
        <Text color="dark.200" fontWeight={700}> {props.title} </Text>
        <Text color="#454545" fontSize=".95em"> {props.desc}</Text>
    </Box>;
}

function SuccessModal(p: SuccessModalProps) {
    return <ConfirmationModal
        isOpen={p.isOpen}
        onClose={p.onClose}
        icon="checked"
        description={<DefaultDescription title={p.title} desc={p.description} textAlign="center" gap="1rem"/>}
        iconColor="var(--chakra-colors-primary-500)"
    >
        {p.trailerComponent}
        <L5Button type="primary" onClick={p.onMainButtonClick ?? p.onClose}>
            {p.mainButtonText ?? "Okay"}
        </L5Button>
    </ConfirmationModal>;
}

export function ConfirmationButton(props: ConfirmationProps<string>) {
    const {isOpen, onOpen, onClose} = useDisclosure();
    const [inputValue, setInputValue] = useState<string|null>(null);
    const error = useMemo(function () {
        if (!inputValue) {
            return;
        }
        if (props.validator) {
            return props.validator(inputValue);
        }
        if (notEmpty(props.placeholder) && !notEmpty(inputValue)) {
            return "This input should not be empty";
        }
        if (props.error) {
            return props.error;
        }
    }, [inputValue, props]);
    const input = useMemo(function () {
        if (props.inputTransformer && inputValue) {
            return props.inputTransformer(inputValue);
        }
        return inputValue ?? "";
    }, [inputValue]);
    function handleConfirm() {
        const trimmed = input.trim();
        if (trimmed || props.placeholder === undefined) {
            props.onSubmit(trimmed);
            setInputValue("");
            onClose();
        }
    }
    function handleCancel() {
        setInputValue("");
        onClose();
    }
    return <>
            <L5Button
                type={props.buttonType ?? "primary"}
                isDisabled={props.isDisabled}
                onClick={onOpen}
            >
                {props.isLoading ? <Spinner/> : props.buttonLabel}
            </L5Button>
            <ConfirmationModal
                isOpen={isOpen}
                size={props.size ?? "lg"}
                onClose={handleCancel}
                title={props.modalTitle}
                icon={props.icon}
                hideIcon={props.hideIcon}
                description={
                    <HStack flexDir="column" alignItems="center" gap={3}>
                        {props.description}
                        {props.placeholder && <FormInput
                            type="text"
                            data={{
                                h: "56px",
                                placeholder: props.placeholder,
                                currentValue: input,
                                error,
                                onUpdate: (v) => setInputValue(v)
                            }}
                        />}
                    </HStack>
                }
            >
                <L5Button type="secondary" onClick={handleCancel}>
                    {props.cancelLabel ?? "Cancel"}
                </L5Button>
                <L5Button
                    onClick={handleConfirm}
                    type="primary"
                    isDisabled={notEmpty(error)}
                >
                    {props.submitLabel}
                </L5Button>
        </ConfirmationModal>
    </>;
};


export function CustoMenu(p: CustoMenuProps) {
    const clone: any = Object.assign({}, p);
    delete clone.children;
    delete clone.items;
    return <Menu {...clone}>
        <MenuButton flex="0 0 auto">{p.children !== undefined
            ? p.children
            : <svg width="24" height="24" aria-labelledby="#menu_title">
                <title id="menu_title">View the available actions</title>
                <use xlinkHref="#v_dots"/>
            </svg>
        }</MenuButton>
        <MenuList>
            {p.items.map( (v, idx) => <MenuItem key={idx} onClick={v.action}>
                <HStack gap=".5rem">
                    <svg width="16" height="16" aria-hidden="true" style={{flex: "0 0 auto"}}>
                        <use xlinkHref={`#${v.icon}`} />
                    </svg>
                    <Text className="one-line" fontSize="0.9em">{v.content}</Text>
                </HStack>
            </MenuItem>)}
        </MenuList>
    </Menu>;
}

export function Description(p: {title: string, children?: ReactNode, description: string}) {
    return <div className="stack">
        <Text fontWeight="bold" color="dark.200" textAlign="center">
            {p.title}
        </Text>
        <Text textAlign="center" style={{textWrap: "pretty"}}>
            {p.description}
        </Text>
        {p.children}
    </div>;
}
export default SuccessModal;
